<template>
    <div>
        <limitless-page-header title="PostNord Prognos"></limitless-page-header>

        <div class="content">
            <div class="card">
                <div class="card-body">
                    <div class="row align-items-end">
                        <div class="col-md-3">
                            <label class="font-weight-semibold">Från</label>
                            <input v-model="filter.date_from" type="date" class="form-control">
                        </div>
                        <div class="col-md-3 mt-2 mt-md-0">
                            <label class="font-weight-semibold">Till</label>
                            <input v-model="filter.date_to" type="date" class="form-control">
                        </div>
                        <div class="col-md-6 text-md-right mt-3 mt-md-0">
                            <limitless-button
                                btn-class="btn-primary"
                                icon="icon-search4"
                                :busy="loading"
                                @click="loadPrognos"
                            >
                                Visa prognos
                            </limitless-button>
                            <limitless-button
                                btn-class="btn-light"
                                icon="icon-file-excel"
                                class="ml-2"
                                :disabled="loading || !hasDates"
                                @click="exportExcel"
                            >
                                Exportera Excel
                            </limitless-button>
                        </div>
                    </div>
                </div>
            </div>

            <div class="card">
                <ul class="nav nav-tabs nav-tabs-highlight mb-0 pl-2 pt-2">
                    <li v-for="tab in tabs" :key="'tab-' + tab.key" class="nav-item">
                        <a href="" class="nav-link" :class="{ active: activeTab === tab.key }" @click.prevent="activeTab = tab.key">
                            {{ tab.label }}
                            <span class="badge badge-light ml-1">{{ totalFor(tab.key) }}</span>
                        </a>
                    </li>
                </ul>

                <div class="card-body border-bottom">
                    <div class="d-flex flex-column flex-md-row">
                        <div>
                            <div class="font-weight-semibold">{{ activeLabel }}</div>
                            <div class="text-muted">{{ filter.date_from }} - {{ filter.date_to }}</div>
                        </div>
                        <div class="ml-md-auto mt-2 mt-md-0">
                            <span
                                v-for="(total, service) in activeTotals"
                                :key="'total-' + service"
                                class="badge badge-light border mr-1"
                            >
                                {{ service }}: {{ formatNumber(total, 0) }}
                            </span>
                        </div>
                    </div>
                </div>

                <rednax-datatable
                    :key="activeTab"
                    :columns="columns"
                    :data="activeRows"
                    :per-page="100"
                    :show-per-page-all="true"
                    sort-by="Group"
                    sort-order="asc"
                    no-wrapper
                >
                    <template #cell_ParcelCount="{ cell }">
                        {{ formatNumber(cell, 0) }}
                    </template>
                </rednax-datatable>
            </div>
        </div>
    </div>
</template>

<script>
import LimitlessButton from '@components/Limitless/LimitlessButton.vue';
import LimitlessPageHeader from '@components/Layout/partials/LimitlessPageHeader';
import RednaxDatatable from '@components/Datatable/RednaxDatatableComponent.vue';
import apiClient from '@services/ApiClient';
import { formatNumber } from '@services/Helpers';

export default {
    name: 'PostnordPrognos',
    components: {
        LimitlessButton,
        LimitlessPageHeader,
        RednaxDatatable,
    },
    data: () => ({
        loading: false,
        activeTab: 'City',
        filter: {
            date_from: '',
            date_to: '',
        },
        report: {
            groups: {},
        },
        tabs: [
            { key: 'City', label: 'Postort' },
            { key: 'Region', label: 'Region' },
            { key: 'Zip', label: 'Postnummer' },
            { key: 'International', label: 'Utlandet' },
        ],
    }),
    mounted() {
        const range = this.defaultDateRange();
        this.filter.date_from = range.date_from;
        this.filter.date_to = range.date_to;
        this.loadPrognos();
    },
    methods: {
        formatNumber,
        loadPrognos() {
            if (!this.hasDates) {
                return;
            }

            this.loading = true;
            apiClient.get('/vue/delivery/postnord-prognos', { params: this.filter })
                .then((response) => {
                    this.report = response.data;
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        exportExcel() {
            const params = new URLSearchParams(this.filter);
            window.location = `/vue/delivery/postnord-prognos/excel?${params.toString()}`;
        },
        defaultDateRange() {
            const dateFrom = new Date();
            const day = dateFrom.getDay();
            const daysUntilNextMonday = day === 1 ? 7 : (8 - day) % 7;
            dateFrom.setDate(dateFrom.getDate() + daysUntilNextMonday);

            const dateTo = new Date(dateFrom);
            dateTo.setDate(dateFrom.getDate() + 6);

            return {
                date_from: this.toDateInputValue(dateFrom),
                date_to: this.toDateInputValue(dateTo),
            };
        },
        toDateInputValue(date) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');

            return `${year}-${month}-${day}`;
        },
        totalFor(group) {
            const groups = this.report.groups || {};
            const totals = groups[group]?.totals || {};
            return this.formatNumber(totals.Totalt || 0, 0);
        },
    },
    computed: {
        hasDates() {
            return !!(this.filter.date_from && this.filter.date_to);
        },
        activeGroup() {
            const groups = this.report.groups || {};
            return groups[this.activeTab] || { label: '', rows: [], totals: {} };
        },
        activeLabel() {
            return this.activeGroup.label || '';
        },
        activeRows() {
            return this.activeGroup.rows || [];
        },
        activeTotals() {
            return this.activeGroup.totals || {};
        },
        columns() {
            const firstColumn = this.activeTab === 'International' ? 'Land/Postnummer' : this.activeLabel;

            return [
                { header: firstColumn, data: 'Group', sortable: true },
                { header: 'Postort', data: 'City', sortable: true },
                { header: 'Land', data: 'Country', sortable: true },
                {
                    header: 'Antal', data: 'ParcelCount', sortable: true, align_right: true,
                },
                { header: 'Tjänst', data: 'Service', sortable: true },
            ];
        },
    },
};
</script>
